import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import firebase from '../utilities/firebase';
import constants from '../utilities/constants';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';

const emptyForm = () => ({
  date: moment().format('YYYY/MM/DD'),
  status: constants.statuses[0],
  status_time: moment().format('YYYY/MM/DD'),
  sub_status: constants.sub_statuses[0],
  sub_status_time: moment().format('YYYY/MM/DD'),
  inDA: false,
  location: 'FL',
  vin: '',
  year: '',
  make: '',
  model: '',
  color: '',
});

const AddCarDialog = ({ open, onClose, serviceOrderId, table = 'orders', initialValues, onSuccess }) => {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [formData, setFormData] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [nextStock, setNextStock] = useState(null);

  useEffect(() => {
    if (open) {
      setFormData({ ...emptyForm(), ...(initialValues || {}) });
      (async () => {
        try {
          const counters = await firebase.firestore().doc('admin/counters').get();
          if (serviceOrderId) {
            const n = counters.data().lastServiceStock + 1;
            setNextStock('SN' + n + '-SR');
          } else {
            const n = counters.data().lastStock + 1;
            setNextStock('SN' + n.toString());
          }
        } catch (e) {
          setNextStock(null);
        }
      })();
    } else {
      setNextStock(null);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    // The VIN is optional — cars often get entered before the VIN is in hand.
    // Add it later from the car page.
    if (!formData.year) newErrors.year = 'Year is required';
    if (!formData.make) newErrors.make = 'Make is required';
    if (!formData.model) newErrors.model = 'Model is required';
    if (!formData.color) newErrors.color = 'Color is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const db = firebase.firestore();
      const counters = await db.doc('admin/counters').get();

      // VINs are always stored upper-cased and trimmed.
      const vin = (formData.vin || '').trim().toUpperCase();

      let stock, carData;
      if (serviceOrderId) {
        const n = counters.data().lastServiceStock + 1;
        stock = 'SN' + n + '-SR';
        carData = {
          ...formData,
          vin,
          stock,
          inDA: false,
          status: 'service',
          sub_status: 'complete',
        };
        await db.doc('cars/' + stock).set(carData);
        await db.doc(table + '/' + serviceOrderId).set({ car: stock }, { merge: true });
        await db.doc('admin/counters').update({ lastServiceStock: n });
      } else {
        const n = counters.data().lastStock + 1;
        stock = 'SN' + n.toString();
        carData = { ...formData, vin, stock };
        await db.doc('cars/' + stock).set(carData);
        await db.doc('admin/counters').update({ lastStock: n });
      }

      onSuccess && onSuccess(stock);
      handleClose();
      if (window.location.pathname === '/') window.location.reload();
    } catch (error) {
      console.error('Error adding car:', error);
      setErrors({ submit: 'Failed to add car. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm());
    setErrors({});
    onClose();
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: myZ }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          Add New Car{nextStock ? ` (${nextStock})` : ''}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="location-label">Location</InputLabel>
              <Select
                labelId="location-label"
                name="location"
                value={formData.location}
                onChange={handleChange}
                label="Location"
                MenuProps={selectMenuProps}
              >
                {constants.makeSelects("locations").map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="vin"
              label="VIN"
              value={formData.vin}
              onChange={handleChange}
              fullWidth
              error={!!errors.vin}
              helperText={errors.vin || 'Optional — add it later from the car page'}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="year"
              label="Year"
              value={formData.year}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.year}
              helperText={errors.year}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="make"
              label="Make"
              value={formData.make}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.make}
              helperText={errors.make}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="model"
              label="Model"
              value={formData.model}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.model}
              helperText={errors.model}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="color"
              label="Color"
              value={formData.color}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.color}
              helperText={errors.color}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Adding...' : 'Add Car'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
};

export default AddCarDialog;
